import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import './StripeForm.css';
import FigoLoadingOverlay from '../FigoLoadingOverlay';
import {
  oneIncLoadDefaultPayments,
  openMultiPoliciesModal,
  paymentMethodPastDueModalVisibleChanged,
  stripeErrorAttempt,
  stripeUpdatePaymentMethodError,
} from '../../../actions';
import {
  PETCLOUD_ORIGIN,
  useGetRecaptchaToken,
} from '../../../hooks/useRecaptchaValidator';
import {
  BANK_ACCOUNT,
  CREDIT_CARD,
  PAYMENT_METHOD_IDS,
  STRIPE_ERROR_CODE,
} from '../../../constants';
import { decodeStripeToken } from '../../../services/stripeUtils';

export const SETUP_INTENT_STATUS = {
  failed: 'requires_payment_method',
  processing: 'processing',
  succeeded: 'succeeded',
};

const StripeWrap = ({
  email,
  name,
  onClose,
  onConfirm = () => { },
  onError = () => { },
  phone,
  selectedPolicy,
}) => {
  const { t } = useTranslation('paymentMethod');
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const handleSubmit = async (event) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    const { setupIntent, error } = await stripe.confirmSetup({
      confirmParams: {
        payment_method_data: {
          billing_details: {
            // TODO: canadians flow no defined, default US
            address: { country: 'US' },
            email,
            name,
            phone,
          },
        },
        return_url: 'https://example/billing/payment-method',
      },
      elements,
      redirect: 'if_required',
    });

    if (error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer
      // (for example, payment details incomplete)

      switch (error.code) {
        case STRIPE_ERROR_CODE.failedVerification:
          dispatch(stripeUpdatePaymentMethodError(error.code));

          dispatch(stripeErrorAttempt({
            email,
            endpoint: 'https://api.stripe.com/v1/setup_intents/',
            error: error?.message,
            requestString: '',
            responseString: JSON.stringify(error),
          }));

          onClose();
          break;

        default:
          break;
      }

      onError(error);
    } else {
      setLoading(true);

      const token = setupIntent?.payment_method;
      const paymentType = await decodeStripeToken(token);

      const stripePaymentData = {
        clientSecret: setupIntent?.client_secret,
        id: setupIntent?.id,
        paymentType,
        policyNumber: selectedPolicy,
        status: setupIntent?.status,
        tokenId: setupIntent?.payment_method,
      };

      onConfirm(stripePaymentData);
    }

    setLoading(false);
  };

  return (
    <>
      <FigoLoadingOverlay visible={loading} />

      <form onSubmit={handleSubmit}>
        <PaymentElement options={{
          defaultValues: {
            billingDetails: {
              // TODO: canadians flow no defined, default US
              address: { country: 'US' },
              email,
              name,
            },
          },
          fields: { billingDetails: { address: { country: 'never' } } },
        }}
        />

        <div className="BtnSaveStripeContainer">
          <button
            className="BtnSaveStripe"
            disabled={!stripe}
            type="submit"
          >
            {t('stripe.addPayMentMethodButton')}
          </button>

          <button
            className="BtnCancelStripe"
            disabled={!stripe}
            onClick={onClose}
            type="button"
          >
            {t('stripe.cancelButton')}
          </button>
        </div>
      </form>
    </>
  );
};

let stripePromise = null;
export const StripeForm = ({
  clientSecret,
  email,
  hasPastPaymentDue = false,
  name,
  onClose = () => { },
  onConfirm = () => { },
  onError = () => { },
  phone,
  selectedPolicy,
  stripeKey,
}) => {
  const dispatch = useDispatch();
  const getRecaptchaToken = useGetRecaptchaToken();

  const store = useSelector(({
    editPayment,
    personalInformation,
    billingPayments,
  }) => ({
    billingPayments,
    editPayment,
    personalInformation,
  }), shallowEqual);

  const {
    billingPayments: { billingDetails },
    editPayment: { policiesList },
  } = store;

  const userInformation = useMemo(() => ({
    address1: store.personalInformation.address1,
    address2: store.personalInformation.address2,
    city: store.personalInformation.city,
    state: store.personalInformation.state,
    zipCode: store.personalInformation.zipCode,
  }), [store.personalInformation]);

  const paymentMethodShared = useCallback((selectedPaymentMethod) => {
    if (!selectedPaymentMethod) {
      return false;
    }

    const otherPoliciesWithSamePayMethod = policiesList
      .filter((policy) => policy && policy.Policy
        .PaymentMethod === selectedPaymentMethod
        && policy.Policy.Number !== selectedPolicy);

    // if same payment method for other policies
    return otherPoliciesWithSamePayMethod.length > 0;
  }, [policiesList, selectedPolicy]);

  const getForceToUpdatePolicies = () => {
    if (!billingDetails) {
      return [];
    }

    const payment = billingDetails.PayMethod === PAYMENT_METHOD_IDS.creditCard
      ? CREDIT_CARD
      : BANK_ACCOUNT;

    const policies = store.editPayment.policiesList
      .filter((policy) => (
        policy.Policy.PaymentMethod === payment
      ));

    return policies;
  };

  const showMultiplePolicyModal = (oneIncPaymentData = {}) => {
    const policies = getForceToUpdatePolicies()
      .map((policy) => (policy.Policy.Number));

    dispatch(openMultiPoliciesModal({
      paymentMethodData: oneIncPaymentData,
      policies,
      sameBillingAddress: true,
      userInformation,
    }));
  };

  const handelOnConfirm = (stripePaymentData) => {
    const hasOnlyOnePolicy = policiesList.length === 1;
    const paymentName = stripePaymentData.paymentType?.paymentName;

    // reacently added payment method
    // or changed from already existing list (not new)
    if (hasOnlyOnePolicy) {
      if (hasPastPaymentDue) {
        dispatch(paymentMethodPastDueModalVisibleChanged({
          requestUpdatePaymentMethod: {
            paymentMethodData: stripePaymentData,
            policies: [],
            policyNumber: selectedPolicy,
            sameBillingAddress: true,
            userInformation,
          },
          visible: true,
        }));

        onClose();
      } else {
        onConfirm({ stripePaymentData });
      }
    } else if (paymentMethodShared(paymentName)) {
      // if payment method is shared with other policies
      showMultiplePolicyModal(stripePaymentData);

      onClose();
    } else {
      // if payment method is not shared with other policies
      onConfirm({ stripePaymentData });
    }
  };

  if (!stripePromise) {
    stripePromise = loadStripe(stripeKey);
  }

  const options = {
    appearance: {
      rules: {
        '.Input': {
          background: 'red',
          border: '1px solid #60666C',
        },
        '.Label': {
          color: 'var(--pc-color-onyx-500)',
          fontFamily: 'Gotham-Rounded',
          fontWeight: 'bold',
        },
      },
      variables: {
        borderRadius: '8px',
        colorDanger: 'var(--pc-color-hydrant-400)',
        colorPrimary: 'var(--pc-color-blue-300)',
        colorText: 'var(--pc-color-onyx-500)',
        fontFamily: 'Gotham-Rounded',
        iconColor: 'var(--pc-color-onyx-200)',
      },
    },
    // passing the SetupIntent's client secret
    clientSecret,
    // Fully customizable with appearance API.
  };

  useEffect(() => {
    getRecaptchaToken().then((token) => {
      dispatch(oneIncLoadDefaultPayments({
        email,
        origin: PETCLOUD_ORIGIN,
        token,
      }));
    });
  }, []);

  return (
    <>
      <Elements options={options} stripe={stripePromise}>
        <StripeWrap
          clientSecret={clientSecret}
          email={email}
          name={name}
          onClose={onClose}
          onConfirm={handelOnConfirm}
          onError={onError}
          phone={phone}
          selectedPolicy={selectedPolicy}
        />
      </Elements>
    </>
  );
};
